import { computed, reactive, readonly } from 'vue'
import {
    streamMessage,
    createStreamController,
    cancelStream,
    dispatchServerChunk,
    subscribeToThread,
    getThreads,
    getThread,
    getChatHistory,
    patchActiveVariant,
    deleteThread as deleteThreadApi,
    renameThread as renameThreadApi,
    sendFeedback as sendFeedbackApi,
} from '@/utils/chatApi'
import { useChatAccessMock } from '@/components/chat/state/useChatAccessMock'
import {
    CHARTABLE_TYPES,
    defaultChartViewConfig,
    inferDefaultChartType,
    normalizeChartViewConfig,
} from '@/components/chat/cards/chartViewConfig'
import {
    applyChartFormatsByTitle,
    applyChartFormatsToMessage,
    applySnapshotToMessage,
    applyVersionsToMessage,
    buildRegenerationPrompt,
    chartableCardIndexes,
    commitNewVersion,
    ensureInitialVersion,
    formatLabelForRegen,
    inferChartFormatsFromFollowingUserMessages,
    parseResponseVariants,
    resetAiMessageForStream,
    syncVersionsFromMeta,
} from '@/components/chat/state/messageVersions'
import {
    finishProgressSteps,
    pushProgressStep,
} from '@/components/chat/state/progressSteps'
import {
    formatSourcesFilterLabel,
    iconForSource,
    normalizeSourceFilter,
    sourceIdsFromFilter,
} from '@/components/chat/state/useSourceSelection'
import { trackPageView } from '@/utils/analytics'

/** Черновик периода для UI (пока пользователь не выбрал фильтр явно). */
export function draftPeriod() {
    const now = new Date()
    return {
        year: now.getFullYear(),
        monthFrom: 1,
        monthTo: now.getMonth() + 1,
    }
}

function isPeriodSet(period) {
    return period != null
        && Number.isFinite(Number(period.year))
        && Number.isFinite(Number(period.monthFrom))
        && Number.isFinite(Number(period.monthTo))
}

function defaultFilterSelection() {
    return {
        region: 'all',
        period: null,
        source: 'all',
        regType: 'all',
    }
}

const MONTHS_SHORT = {
    1: 'ЯНВ',
    2: 'ФЕВ',
    3: 'МАР',
    4: 'АПР',
    5: 'МАЙ',
    6: 'ИЮН',
    7: 'ИЮЛ',
    8: 'АВГ',
    9: 'СЕН',
    10: 'ОКТ',
    11: 'НОЯ',
    12: 'ДЕК',
}

/**
 * Лейблы источников.
 *
 * Заполнение динамическое: при загрузке `getAvailableSources()` колбэк
 * `setAvailableSources()` (см. ниже) добавляет туда все источники из
 * ответа сервера с их `name` и `kind`. Поля `all` и `clickhouse_excel`
 * — legacy фоллбеки, чтобы UI не сломался, если кэш ещё не подтянулся.
 *
 * Структура: code -> { label, kind }.
 */
const SOURCE_LABELS = {
    all: { label: 'Все источники', kind: 'internal' },
    clickhouse_excel: { label: 'Регистрации авто', kind: 'internal' },
}

function sourceLabel(code) {
    if (!code) return ''
    const entry = SOURCE_LABELS[code]
    return entry?.label || code
}

function sourceIcon(code) {
    const entry = SOURCE_LABELS[code]
    return iconForSource(entry ? { id: code, ...entry } : null)
}

/**
 * Регистрация источников из API-ответа `getAvailableSources()`.
 * Вызывается ChatInputArea при монтировании. Сохраняет соответствие
 * `code -> { label, kind }` в SOURCE_LABELS, чтобы `buildFilterChips`
 * мог корректно рисовать чип для любого выбранного источника, не зная
 * заранее его метаданных.
 */
function registerAvailableSources(list) {
    if (!Array.isArray(list)) return
    for (const item of list) {
        if (!item || typeof item.id !== 'string') continue
        SOURCE_LABELS[item.id] = {
            label: item.label || item.name || item.id,
            kind: item.kind || 'internal',
        }
    }
}

const REG_TYPE_LABELS = {
    all: 'Все авто',
    new: 'Новые авто',
    old: 'Б/У авто',
}

let messageCounter = 0
function nextMessageId() {
    messageCounter += 1
    return `msg-${Date.now()}-${messageCounter}`
}

function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

function createUserMessage(text, filterContext = null) {
    return {
        id: nextMessageId(),
        role: 'user',
        status: 'done',
        createdAt: new Date().toISOString(),
        text,
        filterContext,
        source: null,
        sources: [],
        textContent: null,
        textMode: 'plain',
        cards: [],
        lists: [],
        callouts: [],
        followups: null,
        usage: null,
    }
}

function createAiMessage() {
    return {
        id: nextMessageId(),
        role: 'ai',
        status: 'streaming',
        createdAt: new Date().toISOString(),
        source: null,
        sources: [],
        text: '',
        textContent: null,
        textMode: 'plain',
        cards: [],
        lists: [],
        callouts: [],
        followups: null,
        usage: null,
        progress: '',
        progressSteps: [],
        versions: [],
        activeVersionIndex: 0,
    }
}

/**
 * Восстановить сообщение по сохранённым чанкам (после reload или переключения треда).
 * Экспортируется, чтобы read-only страницы (publicReport / shareView / reportRunSnapshot)
 * могли рендерить ту же модель сообщения, что и активный чат, без дублирования логики.
 */
function extractFilterContextFromChunks(chunks) {
    if (!Array.isArray(chunks)) return null
    const chunk = chunks.find(c => c && c.type === 'filter_context')
    if (!chunk) return null
    return {
        selection: chunk.selection || null,
        chips: Array.isArray(chunk.chips) ? chunk.chips : [],
        capturedAt: chunk.captured_at || chunk.capturedAt || null,
    }
}

function buildFilterSnapshot() {
    const selection = cloneFilterSelection(state.filterSelection)
    const chips = buildFilterChips(selection)
    return {
        selection,
        chips,
        capturedAt: new Date().toISOString(),
    }
}

function applyServerChunksToMessage(message, chunks, serverCards) {
    if (Array.isArray(chunks)) {
        for (const raw of chunks) {
            if (!raw || raw.type === 'filter_context' || raw.type === 'response_variants') {
                continue
            }
            dispatchServerChunk(raw, chunk => applyChunk(message, chunk, false))
        }
    }
    if (
        message.role === 'ai'
        && (!message.cards || message.cards.length === 0)
        && Array.isArray(serverCards)
    ) {
        for (const card of serverCards) {
            if (!card) continue
            const cardType = card.cardType || card.card_type
            const rawView = card.viewConfig || card.view_config
            const baseView = CHARTABLE_TYPES.has(cardType)
                ? (rawView
                    ? normalizeChartViewConfig(rawView)
                    : defaultChartViewConfig())
                : undefined
            applyChunk(message, {
                type: 'card',
                cardType,
                payload: card.payload,
                // Только явно сохранённый на сервере тип передаём как displayFormat;
                // иначе пусть applyChunk выберет интуитивный дефолт по структуре.
                displayFormat: rawView ? baseView?.chartType : undefined,
            }, false)
        }
    }
}

export function rebuildMessageFromServer(serverMsg) {
    const filterContext = extractFilterContextFromChunks(serverMsg.chunks)
    const base = serverMsg.role === 'user'
        ? createUserMessage(serverMsg.text || '', filterContext)
        : createAiMessage()
    base.id = `srv-${serverMsg.id}`
    base.serverId = serverMsg.id
    base.status = 'done'
    base.createdAt = serverMsg.created_at || base.createdAt
    base.text = serverMsg.text || ''
    base.feedback = serverMsg.feedback || null

    applyServerChunksToMessage(base, serverMsg.chunks, serverMsg.cards)

    const variantsParsed = parseResponseVariants(serverMsg)
    if (variantsParsed) {
        applyVersionsToMessage(base, variantsParsed)
    } else if (base.role === 'ai') {
        ensureInitialVersion(base)
    }
    return base
}

const MOBILE_BREAKPOINT = 1024

function isMobileViewport() {
    if (typeof window === 'undefined') return false
    return window.innerWidth <= MOBILE_BREAKPOINT
}

const state = reactive({
    threads: [],
    activeThreadId: null,
    filterSelection: cloneFilterSelection(defaultFilterSelection()),
    contextChips: [],
    inputChips: [],
    messages: [],
    isStreaming: false,
    activeMessageId: null,
    sidebarOpen: !isMobileViewport(),
    threadsLoaded: false,
    threadsLoading: false,
    threadsLoadingMore: false,
    threadsSearch: '',
    threadsMeta: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        has_more: false,
    },
    isLoadingHistory: false,
    // Selection mode общий для Share / Admin Publish / My Reports.
    // kind задаёт, что именно делаем с выбранными сообщениями — это влияет на лейблы UI.
    selection: {
        active: false,
        kind: null, // 'share' | 'publish' | 'my-report' | null
        ids: [],
    },
    /** id AI-сообщения, открытого в редакторе (попап) */
    editingMessageId: null,
    /** Причина, почему перегенерация из редактора не стартовала */
    regenerateBlockReason: null,
})

syncFilterChips()

function cloneFilterSelection(value) {
    const defaults = defaultFilterSelection()
    const period = isPeriodSet(value?.period)
        ? {
            year: Number(value.period.year),
            monthFrom: Number(value.period.monthFrom),
            monthTo: Number(value.period.monthTo),
        }
        : null
    return {
        region: value?.region || defaults.region,
        period,
        source: normalizeSourceFilter(value?.source, defaults.source),
        regType: value?.regType || defaults.regType,
    }
}

function periodLabel(period) {
    const from = MONTHS_SHORT[period.monthFrom] || String(period.monthFrom)
    const to = MONTHS_SHORT[period.monthTo] || String(period.monthTo)
    return period.monthFrom === period.monthTo
        ? `${from} ${period.year}`
        : `${from}-${to} ${period.year}`
}

function buildFilterChips(filters) {
    const chips = []

    if (filters.region !== 'all') {
        chips.push({
            id: 'filter-region',
            icon: 'map-pin',
            label: filters.region,
            field: 'region',
            operator: '=',
            value: filters.region,
        })
    }

    if (isPeriodSet(filters.period)) {
        chips.push({
            id: 'filter-period',
            icon: 'calendar',
            label: periodLabel(filters.period),
            field: 'period',
            value: {
                year: filters.period.year,
                month_from: filters.period.monthFrom,
                month_to: filters.period.monthTo,
            },
            sql_hint: `year = ${filters.period.year} AND month BETWEEN ${filters.period.monthFrom} AND ${filters.period.monthTo}`,
        })
    }

    const sourceIds = sourceIdsFromFilter(filters.source)
    if (sourceIds.length > 0) {
        chips.push({
            id: 'filter-source',
            icon: sourceIds.length === 1 ? sourceIcon(sourceIds[0]) : 'database',
            label: formatSourcesFilterLabel(sourceIds, sourceLabel),
            field: 'source',
            value: sourceIds.length === 1 ? sourceIds[0] : sourceIds,
        })
    }

    if (filters.regType !== 'all') {
        chips.push({
            id: 'filter-reg-type',
            icon: 'car',
            label: REG_TYPE_LABELS[filters.regType] || REG_TYPE_LABELS.all,
            field: 'reg_type',
            operator: '=',
            value: filters.regType,
            sql_hint: `reg_type = '${filters.regType}'`,
        })
    }

    return chips
}

function syncFilterChips() {
    const chips = buildFilterChips(state.filterSelection)
    state.inputChips = []
    state.contextChips = chips
}

function allowedSourcesForRequest() {
    return sourceIdsFromFilter(state.filterSelection.source)
}

/**
 * Для перегенерации: восстанавливаем список источников ровно такой же,
 * каким он был на момент исходного запроса. Иначе пользователь жмёт
 * "перегенерировать" — а агент теряет, например, web_search, потому что
 * сейчас в combobox выбран только clickhouse_excel.
 *
 * Приоритет источников:
 *   1) Уникальные коды из фактически использованных aiMsg.sources
 *      (это самый правдивый snapshot — что реально пришло в исходном ответе).
 *   2) Source из filterContext исходного user-сообщения.
 *   3) Текущий combobox-выбор (fallback для совсем старых тредов).
 */
function allowedSourcesForRegen(aiMsg, userMsg) {
    const codes = new Set()
    for (const src of aiMsg?.sources || []) {
        const code = typeof src?.code === 'string' ? src.code.trim() : ''
        if (code) codes.add(code)
    }
    if (codes.size > 0) return [...codes]

    const snapshotIds = sourceIdsFromFilter(userMsg?.filterContext?.selection?.source)
    if (snapshotIds.length > 0) return snapshotIds

    return allowedSourcesForRequest()
}

async function startSelection(kind) {
    state.selection = { active: true, kind, ids: [] }
    // Если в треде есть сообщения, у которых ещё нет serverId (только что отправленные
    // ДО фикса с meta-чанками, либо open в другом треде после reload), — попробуем
    // дозагрузить историю и проставить им serverId. Без этого их нельзя выбрать.
    const needsHydrate = state.activeThreadId && state.messages.some(
        m => (m.role === 'user' || m.role === 'ai') && typeof m.serverId !== 'number'
    )
    if (!needsHydrate) return
    try {
        const history = await getChatHistory(state.activeThreadId)
        const serverMessages = history?.messages || []
        // Сопоставляем «localMsg ↔ server» по порядку внутри роли (user/ai),
        // потому что нумерация id-ов локально и серверно идёт строго в одинаковом порядке.
        const queues = { user: [], ai: [] }
        for (const sm of serverMessages) {
            if (sm.role === 'user' || sm.role === 'ai') queues[sm.role].push(sm)
        }
        for (const lm of state.messages) {
            if (typeof lm.serverId === 'number') continue
            const q = queues[lm.role]
            const sm = q && q.shift()
            if (sm && typeof sm.id === 'number') {
                lm.serverId = sm.id
            }
        }
    } catch (e) {
        // молча: пользователь увидит, что часть сообщений недоступна — это лучше, чем падать.
    }
}

function toggleSelected(messageId) {
    if (!state.selection.active) return
    const ids = new Set(state.selection.ids)
    if (ids.has(messageId)) ids.delete(messageId)
    else ids.add(messageId)
    state.selection = { ...state.selection, ids: Array.from(ids) }
}

function selectAllVisibleMessages() {
    if (!state.selection.active) return
    const ids = state.messages
        .filter(m => typeof m.serverId === 'number')
        .map(m => m.id)
    state.selection = { ...state.selection, ids }
}

function cancelSelection() {
    state.selection = { active: false, kind: null, ids: [] }
}

function getSelectedServerIds() {
    const set = new Set(state.selection.ids)
    return state.messages
        .filter(m => set.has(m.id) && typeof m.serverId === 'number')
        .map(m => m.serverId)
}

let activeController = null
let activeRequestId = null
let historyLoadSeq = 0
const access = useChatAccessMock()

const activeThread = computed(() => {
    return state.threads.find(t => t.id === state.activeThreadId) || null
})

function openSidebar() { state.sidebarOpen = true }
function closeSidebar() { state.sidebarOpen = false }
function closeSidebarOnMobile() {
    if (isMobileViewport()) state.sidebarOpen = false
}
function toggleSidebar() { state.sidebarOpen = !state.sidebarOpen }

const THREADS_PAGE_SIZE = 10

function applyActiveFlags(items) {
    return items.map(t => ({ ...t, active: t.id === state.activeThreadId }))
}

function mergeThreadLists(existing, incoming) {
    const ids = new Set(existing.map(t => t.id))
    const draft = existing.find(t => t.isDraft) || null
    const saved = existing.filter(t => !t.isDraft)
    const appended = incoming.filter(t => !ids.has(t.id))
    const merged = draft ? [draft, ...saved, ...appended] : [...saved, ...appended]
    return applyActiveFlags(merged)
}

async function ensureThreadInList(threadId) {
    if (!threadId || state.threads.some(t => t.id === threadId)) return
    try {
        const thread = await getThread(threadId)
        if (!thread) return
        const without = state.threads.filter(t => t.id !== threadId)
        const draft = without.find(t => t.isDraft)
        const saved = without.filter(t => !t.isDraft)
        state.threads = applyActiveFlags(draft ? [draft, thread, ...saved] : [thread, ...saved])
    } catch (e) {
        /* ignore */
    }
}

async function loadThreads({ reset = true, search } = {}) {
    const nextSearch = search !== undefined ? String(search || '').trim() : state.threadsSearch
    if (reset) {
        state.threadsLoading = true
        state.threadsSearch = nextSearch
    } else {
        if (!state.threadsMeta.has_more || state.threadsLoadingMore) return
        state.threadsLoadingMore = true
    }

    const page = reset ? 1 : state.threadsMeta.current_page + 1

    try {
        const result = await getThreads({
            page,
            per_page: THREADS_PAGE_SIZE,
            search: state.threadsSearch || undefined,
        })
        if (reset) {
            const draft = findDraftThread()
            const items = applyActiveFlags(result.items)
            state.threads = draft ? [draft, ...items] : items
        } else {
            state.threads = mergeThreadLists(state.threads, result.items)
        }
        state.threadsMeta = result.meta
        state.threadsLoaded = true

        if (state.activeThreadId) {
            await ensureThreadInList(state.activeThreadId)
        } else if (reset && state.threads.length > 0) {
            const first = state.threads.find(t => !t.isDraft)
            if (first) await selectThread(first.id)
        }
    } catch (e) {
        if (reset) {
            const draft = findDraftThread()
            state.threads = draft ? [draft] : []
        }
        state.threadsLoaded = true
    } finally {
        state.threadsLoading = false
        state.threadsLoadingMore = false
    }
}

async function loadMoreThreads() {
    await loadThreads({ reset: false })
}

function findDraftThread() {
    return state.threads.find(t => t.isDraft === true) || null
}

/**
 * Постоянная WS-подписка на активный тред, чтобы:
 *   1. Не упустить хвост ответа после перезагрузки страницы (job на сервере
 *      продолжает гнать чанки в канал — фронт переподписывается и хвостовые
 *      чанки догоняют локальное сообщение).
 *   2. Видеть ответы на сообщения, отправленные из другой вкладки.
 *
 * Подписка переоткрывается на каждый selectThread() и закрывается при
 * переходе в другой тред / new dialog. Поверх неё streamMessage()
 * добавляет свой временный listener (через тот же subscribeToThread —
 * Echo делит канал и не открывает второй WS).
 */
let activeThreadUnsubscribe = null

function teardownActiveThreadSubscription() {
    if (activeThreadUnsubscribe) {
        activeThreadUnsubscribe()
        activeThreadUnsubscribe = null
    }
}

function setupActiveThreadSubscription(threadId) {
    teardownActiveThreadSubscription()
    if (!threadId) return
    activeThreadUnsubscribe = subscribeToThread(threadId, (chunk) => {
        // Нас интересуют чанки только для текущего активного треда.
        // streamMessage() уже применит их к локальному aiRef, но если страница
        // перезагрузилась — ai-сообщения в state ещё нет. Подцепим его в этом
        // случае к последнему AI-сообщению со статусом streaming, либо
        // создадим новое (когда отвечает другая вкладка).
        if (state.activeThreadId !== threadId) return
        // Если в этой вкладке идёт локальный стрим — его чанки уже применяет
        // handleChunk внутри streamMessage(). Эта подписка на канал нужна ТОЛЬКО
        // как фолбэк (reload страницы / ответ из другой вкладки), поэтому при
        // активном локальном стриме выходим — иначе каждый чанк применится
        // дважды и push-блоки (карточки, callout, списки) задвоятся.
        if (activeController) return
        const target = findStreamingAiMessage()
        if (!target) {
            // Если это первый чанк не из нашей же streamMessage() — открываем
            // новое AI-сообщение. Иначе streamMessage уже создал его и сам
            // обработал чанк.
            if (chunk.type === 'meta' && typeof chunk.assistantMessageId === 'number') {
                return // не плодим дубль; пользователь увидит ответ после refresh
            }
            return
        }
        applyChunk(target, chunk)
    })
}

function findStreamingAiMessage() {
    for (let i = state.messages.length - 1; i >= 0; i -= 1) {
        const m = state.messages[i]
        if (m.role === 'ai' && m.status === 'streaming') return m
    }
    return null
}

/**
 * Сколько ждать первый WS-чанк, прежде чем пометить «висячий» pending-плейсхолдер
 * как ошибку. Чат-job на бэке обычно отвечает в течение 30-60 секунд; 120 сек —
 * комфортный запас на медленные LLM-сценарии, не вынуждая пользователя
 * сидеть бесконечно перед «крутилкой» в случае, если job упал и в БД ответа
 * никогда не появится.
 */
const PENDING_PLACEHOLDER_TIMEOUT_MS = 120_000

let pendingPlaceholderTimer = null
let pendingPlaceholderThreadId = null
let pendingPlaceholderMessageId = null

function cancelPendingPlaceholderTimer() {
    if (pendingPlaceholderTimer) {
        clearTimeout(pendingPlaceholderTimer)
        pendingPlaceholderTimer = null
    }
    pendingPlaceholderThreadId = null
    pendingPlaceholderMessageId = null
}

/**
 * Если в загруженной истории последнее сообщение — user, а ai-ответа после
 * него нет, добавляем синтетический pending-плейсхолдер: AI-сообщение со
 * status='streaming' и пустым контентом. ChatMessage его рендерит как
 * `<ChatTypingIndicator />`, watchdog там же подсунет фразы-заготовки в
 * шапке («формирую вывод…» и т.п.) — пользователь видит, что ответ ещё
 * генерируется, а не «запрос потерялся».
 *
 * Когда из WS прилетит первый чанк, его подхватит setupActiveThreadSubscription:
 * findStreamingAiMessage() вернёт именно этот плейсхолдер, и `applyChunk` начнёт
 * наполнять его текстом/карточками как обычное стримящееся сообщение.
 *
 * Таймер на 2 минуты страхует от случая, когда job на бэке умер и чанков
 * не будет никогда: по истечении заменяем плейсхолдер на сообщение об ошибке,
 * чтобы пользователь мог переотправить запрос.
 */
function ensurePendingAssistantPlaceholder(threadId) {
    cancelPendingPlaceholderTimer()
    if (!threadId) return
    const messages = state.messages
    if (!messages.length) return
    // Идём с конца: нас интересует, есть ли ai-сообщение ПОСЛЕ последнего user.
    let lastUserIdx = -1
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (messages[i].role === 'user') {
            lastUserIdx = i
            break
        }
    }
    if (lastUserIdx === -1) return
    const hasAiAfter = messages
        .slice(lastUserIdx + 1)
        .some(m => m.role === 'ai')
    if (hasAiAfter) return

    const placeholder = createAiMessage()
    placeholder.isPending = true
    state.messages.push(placeholder)

    pendingPlaceholderThreadId = threadId
    pendingPlaceholderMessageId = placeholder.id
    pendingPlaceholderTimer = setTimeout(() => {
        // Если за TIMEOUT_MS никто не подхватил плейсхолдер (т.е. он всё ещё
        // status='streaming' без контента) — переводим в ошибку с подсказкой.
        //
        // Двойной guard: `pendingPlaceholderThreadId` защищает от случая, когда
        // пользователь успел сменить тред и вернуться обратно — за это время
        // мог быть создан новый плейсхолдер с другим pendingPlaceholderMessageId,
        // и сработавший таймер от старого плейсхолдера не должен трогать новый.
        // `state.activeThreadId` отдельно фильтрует ситуацию «ушёл в другой тред
        // и не вернулся» — там вообще ничего трогать не надо.
        if (pendingPlaceholderThreadId !== threadId || state.activeThreadId !== threadId) {
            cancelPendingPlaceholderTimer()
            return
        }
        const msg = state.messages.find(m => m.id === pendingPlaceholderMessageId)
        if (!msg) {
            cancelPendingPlaceholderTimer()
            return
        }
        const hasContent = (msg.text && msg.text.length > 0)
            || (msg.cards && msg.cards.length > 0)
            || (msg.lists && msg.lists.length > 0)
            || (msg.callouts && msg.callouts.length > 0)
        if (!hasContent && msg.status === 'streaming') {
            msg.status = 'error'
            msg.text = 'Не удалось получить ответ. Возможно, запрос завис — попробуйте отправить ещё раз.'
        }
        cancelPendingPlaceholderTimer()
    }, PENDING_PLACEHOLDER_TIMEOUT_MS)
}

async function selectThread(threadId) {
    // Виртуальная страница «thread» для Метрики: открытие существующего диалога
    // — значимое действие внутри SPA (URL не меняется), фиксируем hit явно.
    trackPageView('/chat/thread')
    state.activeThreadId = threadId
    state.threads = state.threads.map(t => ({ ...t, active: t.id === threadId }))
    closeSidebarOnMobile()
    cancelPendingPlaceholderTimer()
    setupActiveThreadSubscription(threadId)

    const thread = state.threads.find(t => t.id === threadId)
    if (thread?.isDraft) {
        state.isLoadingHistory = false
        state.messages = []
        return
    }

    const loadSeq = ++historyLoadSeq
    state.isLoadingHistory = true
    state.messages = []
    const loadStartedAt = Date.now()
    const MIN_HISTORY_LOADER_MS = 320
    try {
        const history = await getChatHistory(threadId)
        if (loadSeq !== historyLoadSeq) return
        const rawHistory = history.messages || []
        state.messages = rawHistory.map((raw, index) => {
            const rebuilt = rebuildMessageFromServer(raw)
            if (rebuilt.role === 'ai') {
                const inferred = inferChartFormatsFromFollowingUserMessages(rawHistory, index)
                if (inferred.length) {
                    applyChartFormatsByTitle(rebuilt, inferred)
                }
            }
            return rebuilt
        })
        // Если ответа на последний user-вопрос ещё нет (job на сервере крутится),
        // добавим pending-плейсхолдер: пользователь увидит «ассистент печатает…»,
        // а первый же WS-чанк заменит его реальным сообщением. См. ensurePendingAssistantPlaceholder().
        ensurePendingAssistantPlaceholder(threadId)
    } catch (e) {
        // тред мог быть локальным (ещё не сохранён) — это нормально
    } finally {
        if (loadSeq === historyLoadSeq) {
            const elapsed = Date.now() - loadStartedAt
            if (elapsed < MIN_HISTORY_LOADER_MS) {
                await new Promise(resolve => setTimeout(resolve, MIN_HISTORY_LOADER_MS - elapsed))
            }
            if (loadSeq === historyLoadSeq) {
                state.isLoadingHistory = false
            }
        }
    }
}

async function startNewDialog() {
    // Виртуальная страница «new» для Метрики: старт нового диалога.
    trackPageView('/chat/new')
    const existingDraft = findDraftThread()
    if (existingDraft) {
        state.activeThreadId = existingDraft.id
        state.threads = state.threads.map(t => ({ ...t, active: t.id === existingDraft.id }))
        state.messages = []
        closeSidebarOnMobile()
        return
    }

    const id = uuid()
    state.threads = [
        { id, title: 'Новый чат', timeLabel: 'Только что', active: true, isDraft: true },
        ...state.threads.map(t => ({ ...t, active: false })),
    ]
    state.activeThreadId = id
    state.messages = []
    closeSidebarOnMobile()
}

function commitDraftThread() {
    if (!state.activeThreadId) return
    state.threads = state.threads.map(t =>
        t.id === state.activeThreadId && t.isDraft ? { ...t, isDraft: false } : t,
    )
}

async function renameActiveThread(title) {
    if (!state.activeThreadId) return
    const active = state.threads.find(t => t.id === state.activeThreadId)
    state.threads = state.threads.map(t => (t.id === state.activeThreadId ? { ...t, title } : t))
    if (active?.isDraft) return
    try {
        await renameThreadApi(state.activeThreadId, title)
    } catch (e) {
        // молча игнорим
    }
}

async function deleteThread(threadId) {
    const thread = state.threads.find(t => t.id === threadId)
    if (!thread) return

    if (!thread.isDraft) {
        try {
            await deleteThreadApi(threadId)
        } catch (e) {
            /* */
        }
    }

    const wasActive = state.activeThreadId === threadId
    state.threads = state.threads.filter(t => t.id !== threadId)

    if (!wasActive) return

    const nextId = state.threads[0]?.id || null
    state.activeThreadId = nextId
    state.messages = []
    if (nextId) {
        await selectThread(nextId)
    }
}

async function deleteActiveThread() {
    if (!state.activeThreadId) return
    await deleteThread(state.activeThreadId)
}

function removeInputChip(chipId) {
    const next = cloneFilterSelection(state.filterSelection)
    if (chipId === 'filter-region') {
        next.region = 'all'
    } else if (chipId === 'filter-period') {
        next.period = null
    } else if (chipId === 'filter-source') {
        next.source = 'all'
    } else if (chipId === 'filter-reg-type') {
        next.regType = 'all'
    } else {
        state.inputChips = state.inputChips.filter(c => c.id !== chipId)
        return
    }
    updateFilters(next)
}

function removeContextChip(chipId) {
    removeInputChip(chipId)
}

function updateFilters(filters) {
    state.filterSelection = cloneFilterSelection(filters)
    syncFilterChips()
}

function stopStreaming() {
    if (activeController) {
        activeController.abort()
        activeController = null
    }
    // Дёргаем backend cancel — он остановит FastAPI-стрим и отметит usage.
    if (activeRequestId) {
        const rid = activeRequestId
        cancelStream(rid).catch(() => { /* фронт-аборта достаточно для UX */ })
        activeRequestId = null
    }
    if (state.activeMessageId) {
        const msg = state.messages.find(m => m.id === state.activeMessageId)
        if (msg) {
            msg.status = 'stopped'
        }
    }
    state.isStreaming = false
    state.activeMessageId = null
}

// Дешёвый стабильный ключ для дедупа renderable-блоков. Сравниваем только
// чанки из одного источника (тот же broadcast), поэтому порядок ключей
// совпадает и обычного JSON.stringify достаточно.
function stableStringify(value) {
    if (value === undefined || value === null) return ''
    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

function applyChunk(message, chunk, trackUsage = true) {
    switch (chunk.type) {
        case 'meta':
            if (chunk.requestId) {
                message.requestId = chunk.requestId
                activeRequestId = chunk.requestId
            }
            break
        case 'progress':
            // pushProgressStep сам обновит message.progress и накопит шаги
            // таймлайна процесса (план → сбор по источникам → синтез → …).
            pushProgressStep(message, chunk)
            break
        case 'source': {
            const source = chunk.source || {}
            if (!Array.isArray(message.sources)) {
                message.sources = []
            }
            // Для веб-поиска приходит несколько чанков с одним code, но разными url —
            // дедуп должен это учитывать, иначе все ссылки схлопнутся в одну.
            const dedupKey = `${source.code || source.type || ''}|${source.url || source.period || ''}`
            const exists = message.sources.some(
                s => `${s.code || s.type || ''}|${s.url || s.period || ''}` === dedupKey
            )
            if (!exists) {
                message.sources.push(source)
            }
            message.source = source
            break
        }
        case 'text:start':
            message.textMode = chunk.mode
            message.text = ''
            message.textContent = chunk.content || null
            break
        case 'text:delta':
            message.text = typeof chunk.text === 'string'
                ? chunk.text
                : (message.text || '') + (chunk.delta || '')
            break
        case 'text:end':
            if (typeof chunk.text === 'string') {
                message.text = chunk.text
            }
            if (chunk.content) {
                message.textContent = chunk.content
            }
            break
        case 'card': {
            const cardIndex = message.cards.length
            const payload = chunk.payload || {}
            // Страховка от дублей: один и тот же card может прийти повторно
            // (переподключение/реплей канала после reload). Идентичный по
            // cardType+payload блок не добавляем второй раз.
            const cardSig = `${chunk.cardType}|${stableStringify(payload)}`
            if (message.cards.some(c => `${c.cardType}|${stableStringify(c.payload)}` === cardSig)) {
                break
            }
            const chartableOrdinal = message.cards.filter(c =>
                CHARTABLE_TYPES.has(c.cardType),
            ).length
            const pendingFmt = message._pendingChartFormats?.find(f =>
                f.chartableIndex === chartableOrdinal,
            ) || message._pendingChartFormats?.find(f => f.cardIndex === cardIndex)
            const displayFormat = pendingFmt?.chartType
                || payload.displayFormat
                || chunk.displayFormat
            const baseView = CHARTABLE_TYPES.has(chunk.cardType)
                ? defaultChartViewConfig()
                : undefined
            // Если модель не задала displayFormat — выбираем интуитивный дефолт
            // по структуре: рейтинг (категориальная ось) → столбцы, ряд → линия.
            const resolvedFormat = displayFormat
                || (baseView ? inferDefaultChartType({ payload }) : null)
            message.cards.push({
                cardType: chunk.cardType,
                payload,
                viewConfig: baseView
                    ? normalizeChartViewConfig({ ...baseView, chartType: resolvedFormat || baseView.chartType })
                    : baseView,
            })
            break
        }
        case 'list': {
            const listSig = `${chunk.title || ''}|${stableStringify(chunk.items)}`
            if (message.lists.some(l => `${l.title || ''}|${stableStringify(l.items)}` === listSig)) {
                break
            }
            message.lists.push({ title: chunk.title, items: chunk.items })
            break
        }
        case 'callout': {
            const calloutSig = `${chunk.tone || ''}|${chunk.title || ''}|${stableStringify(chunk.content)}`
            const exists = message.callouts.some(
                c => `${c.tone || ''}|${c.title || ''}|${stableStringify(c.content)}` === calloutSig,
            )
            if (exists) {
                break
            }
            message.callouts.push({
                tone: chunk.tone,
                title: chunk.title,
                content: chunk.content,
            })
            break
        }
        case 'followups':
            message.followups = { title: chunk.title, actions: chunk.actions }
            break
        case 'usage':
            message.usage = {
                sourcesCount: chunk.sourcesCount,
                reportId: chunk.reportId,
                durationMs: chunk.durationMs,
                tokensUsed: chunk.tokensUsed,
            }
            if (trackUsage && chunk.tokensUsed) {
                access.trackTokenUsage(chunk.tokensUsed)
            }
            break
        case 'done':
            message.status = 'done'
            message.progress = ''
            finishProgressSteps(message)
            // Если за весь стрим не пришло ни текста, ни карточек — это «висели точки».
            // Покажем пользователю человекочитаемое сообщение об ошибке.
            if (
                !message.text
                && (!message.textContent || !message.textContent.length)
                && (!message.cards || !message.cards.length)
                && (!message.lists || !message.lists.length)
                && (!message.callouts || !message.callouts.length)
                && (!message.followups?.actions || !message.followups.actions.length)
            ) {
                message.status = 'error'
                message.text = 'Не удалось получить ответ. Попробуйте переформулировать запрос.'
            }
            break
        case 'error':
            message.status = 'error'
            message.progress = ''
            finishProgressSteps(message)
            message.text = chunk.message || message.text || 'Произошла ошибка при обработке запроса.'
            break
        case 'limit':
            message.status = 'error'
            message.text = chunk.reason === 'requests_exhausted'
                ? 'Лимит запросов на сегодня исчерпан.'
                : chunk.reason === 'tokens_exhausted'
                ? 'Лимит токенов исчерпан. Купите аддон или перейдите на план повыше.'
                : chunk.reason === 'plan_expired'
                ? 'Срок действия тарифа истёк. Выберите тариф, чтобы продолжить.'
                : 'Чат недоступен на текущем плане.'
            break
        default:
            break
    }
}

function historyForServer(messages = state.messages) {
    return messages
        .filter(m => m.role === 'user' || m.role === 'ai')
        .slice(-12)
        .map(m => {
            const entry = { role: m.role, text: typeof m.text === 'string' ? m.text : '' }
            // Прокидываем feedback AI-ответа в историю — бэкенд/агент может
            // учитывать оценки пользователя при формировании следующих ответов.
            if (m.role === 'ai' && m.feedback) {
                entry.feedback = m.feedback // 'up' | 'down'
            }
            return entry
        })
}

async function sendMessage(text) {
    const trimmed = (text || '').trim()
    if (!trimmed || state.isStreaming) {
        return
    }
    access.refreshAuthState()
    if (access.isLimitReached.value) {
        return
    }

    if (!state.activeThreadId) {
        await startNewDialog()
    }
    commitDraftThread()

    // Авто-название ветки: если у активного треда дефолтный title — сразу подменим
    // на сокращённый текст первого запроса. Бэк делает то же самое в ChatMessageStore,
    // но фронту нужен моментальный фидбек в сайдбаре, без ожидания refetch.
    const active = state.threads.find(t => t.id === state.activeThreadId)
    if (active && (active.title === 'Новый чат' || !active.title)) {
        const autoTitle = trimmed.length > 80 ? trimmed.slice(0, 80) + '…' : trimmed
        state.threads = state.threads.map(t =>
            t.id === state.activeThreadId ? { ...t, title: autoTitle } : t,
        )
    }

    // Виртуальная страница «поиск» для Яндекс.Метрики — считаем каждый новый
    // пользовательский запрос отдельным «просмотром» в отчётах, чтобы аналитик
    // видел активность внутри чата (SPA не создаёт реальных смен URL).
    trackPageView('/chat/search')

    const filterSnapshot = buildFilterSnapshot()
    const userMsg = createUserMessage(trimmed, filterSnapshot)
    const aiMsg = createAiMessage()
    const history = historyForServer()
    state.messages.push(userMsg)
    state.messages.push(aiMsg)
    const userRef = state.messages[state.messages.length - 2]
    const aiRef = state.messages[state.messages.length - 1]
    state.isStreaming = true
    state.activeMessageId = aiRef.id

    // Перехватываем meta-чанки, чтобы проставить БД-id (serverId) на свежесозданные
    // локальные сообщения. Без этого их нельзя выбрать в selection mode (share/publish),
    // потому что бэк требует целочисленные message_ids.
    function handleChunk(chunk) {
        if (chunk && chunk.type === 'meta') {
            if (typeof chunk.userMessageId === 'number') {
                userRef.serverId = chunk.userMessageId
            }
            if (typeof chunk.assistantMessageId === 'number') {
                aiRef.serverId = chunk.assistantMessageId
            }
            if (chunk.responseVariants) {
                syncVersionsFromMeta(aiRef, chunk.responseVariants, aiRef._pendingChartFormats)
            }
        }
        applyChunk(aiRef, chunk)
    }

    activeController = createStreamController()
    try {
        await streamMessage({
            text: trimmed,
            threadId: state.activeThreadId,
            context: state.contextChips,
            filterSnapshot,
            allowedSources: allowedSourcesForRequest(),
            history,
            signal: activeController.signal,
            onChunk: handleChunk,
        })
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('chat stream failed', error)
        aiRef.status = 'error'
        aiRef.text = error?.message || 'Не удалось получить ответ. Попробуйте ещё раз.'
    } finally {
        access.consumeRequest()
        if (aiRef.status === 'streaming') {
            aiRef.status = 'done'
        }
        if (aiRef.status === 'done' && !aiRef.versions?.length) {
            ensureInitialVersion(aiRef)
        }
        state.isStreaming = false
        state.activeMessageId = null
        activeController = null
        activeRequestId = null
    }
}

function filterSnapshotForUserMessage(userMsg) {
    if (!userMsg?.filterContext) {
        return buildFilterSnapshot()
    }
    return {
        selection: cloneFilterSelection(userMsg.filterContext.selection || state.filterSelection),
        chips: Array.isArray(userMsg.filterContext.chips)
            ? [...userMsg.filterContext.chips]
            : buildFilterChips(userMsg.filterContext.selection || state.filterSelection),
        capturedAt: userMsg.filterContext.capturedAt || new Date().toISOString(),
    }
}

async function runStreamIntoAiMessage({
    aiMsg,
    text,
    history,
    filterSnapshot,
    regenLabel = null,
    regenerateAssistantMessageId = null,
    allowedSourcesOverride = null,
}) {
    if (regenLabel) {
        aiMsg._pendingRegenLabel = regenLabel
    }
    state.isStreaming = true
    state.activeMessageId = aiMsg.id

    function handleChunk(chunk) {
        if (chunk && chunk.type === 'meta') {
            if (typeof chunk.assistantMessageId === 'number') {
                aiMsg.serverId = chunk.assistantMessageId
            }
            if (chunk.responseVariants) {
                syncVersionsFromMeta(aiMsg, chunk.responseVariants, aiMsg._pendingChartFormats)
            }
        }
        applyChunk(aiMsg, chunk)
    }

    activeController = createStreamController()
    try {
        await streamMessage({
            text,
            threadId: state.activeThreadId,
            context: Array.isArray(filterSnapshot?.chips) && filterSnapshot.chips.length
                ? filterSnapshot.chips
                : state.contextChips,
            filterSnapshot,
            allowedSources: Array.isArray(allowedSourcesOverride)
                ? allowedSourcesOverride
                : allowedSourcesForRequest(),
            history,
            regenerateAssistantMessageId: regenerateAssistantMessageId ?? undefined,
            variantFormatLabel: regenLabel ?? undefined,
            variantChartFormats: aiMsg._pendingChartFormats?.length
                ? aiMsg._pendingChartFormats
                : undefined,
            signal: activeController.signal,
            onChunk: handleChunk,
        })
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('chat stream failed', error)
        aiMsg.status = 'error'
        aiMsg.text = error?.message || 'Не удалось получить ответ. Попробуйте ещё раз.'
        aiMsg._pendingRegenLabel = null
    } finally {
        access.consumeRequest()
        if (aiMsg.status === 'streaming') {
            aiMsg.status = 'done'
        }
        if (aiMsg.status === 'done') {
            if (aiMsg._pendingChartFormats?.length) {
                applyChartFormatsToMessage(aiMsg, aiMsg._pendingChartFormats)
            }
            if (aiMsg._pendingRegenLabel) {
                commitNewVersion(aiMsg, aiMsg._pendingRegenLabel)
            } else if (!aiMsg.versions?.length) {
                ensureInitialVersion(aiMsg)
            }
            aiMsg._pendingRegenLabel = null
            aiMsg._pendingChartFormats = null
        }
        state.isStreaming = false
        state.activeMessageId = null
        activeController = null
        activeRequestId = null
    }
}

function findMessageById(messageId) {
    return state.messages.find(m => m.id === messageId) || null
}

function updateCardViewConfig(messageId, cardIndex, viewConfig) {
    const msg = findMessageById(messageId)
    if (!msg?.cards?.[cardIndex]) return
    msg.cards[cardIndex].viewConfig = normalizeChartViewConfig(viewConfig)
}

function applyMessageVisualization(messageId, patches) {
    const msg = findMessageById(messageId)
    if (!msg || !Array.isArray(patches)) return
    for (const patch of patches) {
        const idx = patch.cardIndex
        if (typeof idx !== 'number' || !msg.cards[idx]) continue
        msg.cards[idx].viewConfig = normalizeChartViewConfig(patch.viewConfig)
    }
}

async function setMessageVersion(messageId, index) {
    const msg = findMessageById(messageId)
    if (!msg?.versions?.length || index < 0 || index >= msg.versions.length) {
        return
    }
    applySnapshotToMessage(msg, msg.versions[index])
    msg.activeVersionIndex = index

    if (typeof msg.serverId === 'number' && state.activeThreadId) {
        try {
            const updated = await patchActiveVariant(state.activeThreadId, msg.serverId, index)
            if (updated) {
                const parsed = parseResponseVariants(updated)
                if (parsed) {
                    applyVersionsToMessage(msg, parsed)
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('patchActiveVariant failed', error)
        }
    }
}

function stepMessageVersion(messageId, delta) {
    const msg = findMessageById(messageId)
    if (!msg?.versions?.length) return
    const next = (msg.activeVersionIndex ?? 0) + delta
    setMessageVersion(messageId, next)
}

function openMessageEditor(messageId) {
    const msg = findMessageById(messageId)
    if (!msg || msg.role !== 'ai' || msg.status !== 'done') return
    state.regenerateBlockReason = null
    state.editingMessageId = messageId
}

function closeMessageEditor() {
    state.editingMessageId = null
    state.regenerateBlockReason = null
}

async function regenerateAiMessage(messageId, { chartFormats = [], extraInstructions = '' } = {}) {
    state.regenerateBlockReason = null

    if (state.isStreaming) {
        state.regenerateBlockReason = 'streaming'
        return { ok: false, reason: 'streaming' }
    }

    const aiIdx = state.messages.findIndex(m => m.id === messageId)
    const aiMsg = aiIdx >= 0 ? state.messages[aiIdx] : null
    if (!aiMsg || aiMsg.role !== 'ai') {
        state.regenerateBlockReason = 'not_found'
        return { ok: false, reason: 'not_found' }
    }

    const userMsg = [...state.messages.slice(0, aiIdx)].reverse().find(m => m.role === 'user')
    if (!userMsg?.text?.trim()) {
        state.regenerateBlockReason = 'no_user_message'
        return { ok: false, reason: 'no_user_message' }
    }

    access.refreshAuthState()
    if (access.isLimitReached.value) {
        state.regenerateBlockReason = 'limit'
        return { ok: false, reason: 'limit' }
    }

    trackPageView('/chat/regenerate')

    if (!state.activeThreadId) {
        await startNewDialog()
    }
    commitDraftThread()

    ensureInitialVersion(aiMsg)

    const chartFormatsResolved = (chartFormats.length
        ? chartFormats
        : (aiMsg.cards || [])
            .map((card, index) => ({
                cardIndex: index,
                chartableIndex: chartableCardIndexes(aiMsg.cards).indexOf(index),
                title: card.payload?.title || `График ${index + 1}`,
                chartType: inferDefaultChartType(card),
            }))
            .filter(({ cardIndex }) => CHARTABLE_TYPES.has(aiMsg.cards[cardIndex]?.cardType))
    ).map((fmt, ordinal) => ({
        ...fmt,
        chartableIndex: Number.isFinite(fmt.chartableIndex) ? fmt.chartableIndex : ordinal,
    }))

    const extra = (extraInstructions || '').trim()
    const formatLabel = formatLabelForRegen(chartFormatsResolved)
    const regenLabel = extra && formatLabel === 'Перегенерация'
        ? 'Редактирование'
        : formatLabel
    const regenText = buildRegenerationPrompt(userMsg.text, chartFormatsResolved, extra)
    const filterSnapshot = filterSnapshotForUserMessage(userMsg)
    const history = historyForServer(state.messages.slice(0, aiIdx))

    resetAiMessageForStream(aiMsg)
    aiMsg._pendingChartFormats = chartFormatsResolved

    await runStreamIntoAiMessage({
        aiMsg,
        text: regenText,
        history,
        filterSnapshot,
        regenLabel,
        regenerateAssistantMessageId: typeof aiMsg.serverId === 'number' ? aiMsg.serverId : null,
        allowedSourcesOverride: allowedSourcesForRegen(aiMsg, userMsg),
    })
    return { ok: true }
}

/**
 * Установить/сбросить feedback на AI-сообщение.
 * Сохраняет локально (message.feedback) и отправляет на бэкенд (fire-and-forget).
 *
 * @param {string} messageId — локальный id сообщения
 * @param {'up'|'down'|null} value — оценка (null = сброс)
 */
function setFeedback(messageId, value) {
    const msg = state.messages.find(m => m.id === messageId)
    if (!msg || msg.role !== 'ai') return

    // Toggle: повторный клик на ту же оценку — сброс
    const newValue = msg.feedback === value ? null : value
    msg.feedback = newValue

    // Fire-and-forget отправка на бэкенд
    if (typeof msg.serverId === 'number' && state.activeThreadId) {
        sendFeedbackApi(state.activeThreadId, msg.serverId, newValue).catch(() => {
            // Бэк может быть не готов — молча проглатываем
        })
    }
}

function regenerateLast() {
    const lastAi = [...state.messages].reverse().find(m => m.role === 'ai')
    if (lastAi) {
        regenerateAiMessage(lastAi.id)
        return
    }
    const lastUser = [...state.messages].reverse().find(m => m.role === 'user')
    if (!lastUser) return
    const idx = state.messages.findIndex(m => m.id === lastUser.id)
    state.messages = state.messages.slice(0, idx)
    sendMessage(lastUser.text)
}

export function useChatStore() {
    return {
        state: readonly(state),
        activeThread,
        access,
        sendMessage,
        stopStreaming,
        regenerateLast,
        regenerateAiMessage,
        openMessageEditor,
        closeMessageEditor,
        setMessageVersion,
        stepMessageVersion,
        selectThread,
        startNewDialog,
        renameActiveThread,
        deleteActiveThread,
        deleteThread,
        loadThreads,
        loadMoreThreads,
        removeInputChip,
        removeContextChip,
        updateFilters,
        registerAvailableSources,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        startSelection,
        toggleSelected,
        selectAllVisibleMessages,
        cancelSelection,
        getSelectedServerIds,
        updateCardViewConfig,
        applyMessageVisualization,
        setFeedback,
    }
}
